import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useCreateClient, useUpdateClient } from '@/hooks/queries/useClients';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/components/ui/toast/useToast';
import { WILAYAS } from '@/lib/utils';
import type { Client } from '@lebusiness/types';
import { CLIENT_TYPE_LABELS, type ClientType } from '@/types/client';

const clientSchema = z.object({
  name: z.string().min(1, 'الاسم مطلوب / Nom requis'),
  type: z.enum(['client', 'supplier', 'both']).default('client'),
  phone: z.string().min(1, 'رقم الهاتف مطلوب / Téléphone requis'),
  email: z.string().email('بريد إلكتروني غير صالح / Email invalide').optional().or(z.literal('')),
  address: z.string().optional(),
  wilaya: z.string().min(1, 'الولاية مطلوبة / Wilaya requise'),
  taxNumber: z.string().optional(), // NIF
  commercialRegister: z.string().optional(), // RC
  nisNumber: z.string().optional(), // NIS
  articleNumber: z.string().optional(), // AI
  creditLimit: z.number().min(0, 'الحد الائتماني يجب أن يكون موجب / Limite doit être positive').optional(),
  notes: z.string().optional(),
});

type ClientFormData = z.infer<typeof clientSchema>;

interface ClientFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
  onSuccess?: () => void;
}

export function ClientForm({ open, onOpenChange, client, onSuccess }: ClientFormProps) {
  const { company } = useAuthStore();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      type: 'client',
      wilaya: 'Alger',
      creditLimit: 0,
    },
  });

  // Reset form when dialog opens or client changes
  useEffect(() => {
    if (open) {
      if (client) {
        // Editing existing client
        reset({
          name: client.name,
          type: client.type || 'client',
          phone: client.phone || '',
          email: client.email || '',
          address: client.address || '',
          wilaya: client.wilaya || 'Alger',
          taxNumber: client.tax_id || client.taxNumber || '',
          commercialRegister: client.nrc || client.commercialRegister || '',
          nisNumber: client.nis || client.nisNumber || '',
          articleNumber: client.articleNumber || '',
          creditLimit: client.credit_limit || client.creditLimit || 0,
          notes: client.notes || '',
        });
      } else {
        // New client
        reset({
          name: '',
          type: 'client',
          phone: '',
          email: '',
          address: '',
          wilaya: 'Alger',
          taxNumber: '',
          commercialRegister: '',
          nisNumber: '',
          articleNumber: '',
          creditLimit: 0,
          notes: '',
        });
      }
    }
  }, [open, client, reset]);

  const onSubmit = async (data: ClientFormData) => {
    if (!company?.id) return;

    try {
      const clientData = {
        company_id: company.id,
        name: data.name,
        type: data.type,
        phone: data.phone,
        email: data.email || null,
        address: data.address || null,
        wilaya: data.wilaya,
        tax_id: data.taxNumber || null,
        nrc: data.commercialRegister || null,
        nis: data.nisNumber || null,
        article_number: data.articleNumber || null,
        credit_limit: data.creditLimit || null,
        payment_terms: null,
        notes: data.notes || null,
      };

      if (client) {
        await updateClient.mutateAsync({ id: client.id, ...clientData });
        toast.success(
          'تم التحديث بنجاح / Modifié avec succès',
          'تم تحديث العميل / Le client a été modifié'
        );
      } else {
        await createClient.mutateAsync(clientData);
        toast.success(
          'تم الإضافة بنجاح / Ajouté avec succès',
          'تم إضافة العميل / Le client a été ajouté'
        );
      }

      onSuccess?.();
      onOpenChange(false);
      reset();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(
        'خطأ / Erreur',
        error instanceof Error ? error.message : 'فشل حفظ العميل / Échec de l\'enregistrement'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {client
              ? 'تعديل العميل / Modifier le client'
              : 'عميل جديد / Nouveau client'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">
              المعلومات الأساسية / Informations de base
            </h3>

            {/* TYPE SELECTOR - NEW! */}
            <div className="space-y-2">
              <Label>النوع / Type *</Label>
              <Select
                value={watch('type')}
                onValueChange={(value) => setValue('type', value as ClientType)}
              >
                <SelectTrigger className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">👤</span>
                      <div>
                        <div className="font-medium">{CLIENT_TYPE_LABELS.client.ar} / {CLIENT_TYPE_LABELS.client.fr}</div>
                        <div className="text-xs text-gray-500">عميل فقط / Client uniquement</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="supplier">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🏭</span>
                      <div>
                        <div className="font-medium">{CLIENT_TYPE_LABELS.supplier.ar} / {CLIENT_TYPE_LABELS.supplier.fr}</div>
                        <div className="text-xs text-gray-500">مورد فقط / Fournisseur uniquement</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="both">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🔄</span>
                      <div>
                        <div className="font-medium">{CLIENT_TYPE_LABELS.both.ar} / {CLIENT_TYPE_LABELS.both.fr}</div>
                        <div className="text-xs text-gray-500">عميل ومورد / Client et fournisseur</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-sm text-red-600">{errors.type.message}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                اختر إذا كان عميل أو مورد أو كلاهما / Choisissez si c'est un client, fournisseur ou les deux
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">الاسم / Nom *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="شركة النجاح / Société El Nadjah"
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">رقم الهاتف / Téléphone *</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder="0551234567"
                />
                {errors.phone && (
                  <p className="text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">البريد الإلكتروني / Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="contact@example.dz"
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">العنوان / Adresse</Label>
              <Input
                id="address"
                {...register('address')}
                placeholder="15 شارع ديدوش مراد / 15 Rue Didouche Mourad"
              />
            </div>

            <div className="space-y-2">
              <Label>الولاية / Wilaya *</Label>
              <Select
                value={watch('wilaya')}
                onValueChange={(value) => setValue('wilaya', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {WILAYAS.map((wilaya) => (
                    <SelectItem key={wilaya.code} value={wilaya.nameFr}>
                      {wilaya.code} - {wilaya.nameAr} / {wilaya.nameFr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.wilaya && (
                <p className="text-sm text-red-600">{errors.wilaya.message}</p>
              )}
            </div>
          </div>

          {/* Fiscal Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">
              المعلومات الجبائية / Informations fiscales
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="taxNumber">
                  رقم التعريف الجبائي / NIF
                  <span className="text-xs text-gray-500 ml-2">(15 chiffres)</span>
                </Label>
                <Input
                  id="taxNumber"
                  {...register('taxNumber')}
                  placeholder="123456789012345"
                  maxLength={15}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="commercialRegister">
                  السجل التجاري / RC
                  <span className="text-xs text-gray-500 ml-2">(ex: 01/00-1234567)</span>
                </Label>
                <Input
                  id="commercialRegister"
                  {...register('commercialRegister')}
                  placeholder="01/00-1234567"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nisNumber">
                  رقم التعريف الإحصائي / NIS
                </Label>
                <Input
                  id="nisNumber"
                  {...register('nisNumber')}
                  placeholder="123456789012345"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="articleNumber">
                  رقم المادة / AI
                </Label>
                <Input
                  id="articleNumber"
                  {...register('articleNumber')}
                  placeholder="12345678901234"
                />
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">
              المعلومات المالية / Informations financières
            </h3>

            <div className="space-y-2">
              <Label htmlFor="creditLimit">
                الحد الائتماني / Limite de crédit (DZD)
              </Label>
              <Input
                id="creditLimit"
                type="number"
                step="0.01"
                {...register('creditLimit', { valueAsNumber: true })}
                placeholder="0.00"
              />
              {errors.creditLimit && (
                <p className="text-sm text-red-600">{errors.creditLimit.message}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                الحد الأقصى للائتمان المسموح به / Montant maximum de crédit autorisé
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">ملاحظات / Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="ملاحظات إضافية / Notes supplémentaires..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              إلغاء / Annuler
            </Button>
            <Button
              type="submit"
              disabled={createClient.isPending || updateClient.isPending}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              {(createClient.isPending || updateClient.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {client ? 'حفظ / Enregistrer' : 'إضافة / Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}